// src/platform/storage.ts
// Hybrid storage: localStorage for sync reads (interceptors), Preferences for encrypted native persistence

import { isNative } from './platform';

let Preferences: typeof import('@capacitor/preferences').Preferences | null = null;

/** Call once before React renders (in main.tsx). Hydrates localStorage from Preferences on native. */
export async function hydrateStorageFromNative(): Promise<void> {
  if (!isNative()) return;
  const mod = await import('@capacitor/preferences');
  Preferences = mod.Preferences;

  const keys = [
    'access_token', 'refresh_token', 'expires_at', 'user_id',
    'token_lifetime_sec', 'last_active_at',
    'mailander_biometrics_enabled', 'mailander_biometrics_type', 'mailander_auto_prompt_passkey',
    'conversation_id', 'theme_id', 'mailander_has_account',
  ];

  for (const key of keys) {
    const { value } = await Preferences.get({ key });
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  }
}

/** Write to localStorage (sync) AND Preferences (async, native only). */
export function storageSet(key: string, value: string): void {
  localStorage.setItem(key, value);
  if (isNative() && Preferences) {
    Preferences.set({ key, value }).catch(() => {});
  }
}

/** Remove from localStorage AND Preferences. */
export function storageRemove(key: string): void {
  localStorage.removeItem(key);
  if (isNative() && Preferences) {
    Preferences.remove({ key }).catch(() => {});
  }
}

/** Read from localStorage (sync — always available after hydration). */
export function storageGet(key: string): string | null {
  return localStorage.getItem(key);
}

/** Clear all. */
export function storageClear(): void {
  localStorage.clear();
  if (isNative() && Preferences) {
    Preferences.clear().catch(() => {});
  }
}
